/**
 * Where the tour's callout goes relative to the spotlighted panel: above a low
 * panel (the bottom dock, reference 07), beside a side panel, or inside a
 * panel too big to leave room around it (the viewport). Always kept on screen.
 */

export interface Box {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface Placement {
  readonly side: "above" | "below" | "right" | "left" | "inside";
  readonly left: number;
  readonly top: number;
}

/** Gap between the callout and its target, and the minimum screen margin. */
const GAP = 12;
const MARGIN = 8;

export function calloutPlacement(
  target: Box,
  callout: { readonly width: number; readonly height: number },
  screen: { readonly width: number; readonly height: number },
): Placement {
  const room = {
    above: target.top - GAP - MARGIN,
    below: screen.height - (target.top + target.height) - GAP - MARGIN,
    right: screen.width - (target.left + target.width) - GAP - MARGIN,
    left: target.left - GAP - MARGIN,
  };
  const centeredTop = target.top + target.height / 2 - callout.height / 2;

  // Wide, short targets take the callout above or below; tall ones beside.
  const wide = target.width >= target.height;
  let placement: Placement;
  if (wide && room.above >= callout.height) {
    placement = { side: "above", left: target.left, top: target.top - GAP - callout.height };
  } else if (wide && room.below >= callout.height) {
    placement = { side: "below", left: target.left, top: target.top + target.height + GAP };
  } else if (room.right >= callout.width) {
    placement = { side: "right", left: target.left + target.width + GAP, top: centeredTop };
  } else if (room.left >= callout.width) {
    placement = { side: "left", left: target.left - GAP - callout.width, top: centeredTop };
  } else {
    // No room outside: inside the target, near its bottom, like a viewport HUD.
    placement = {
      side: "inside",
      left: target.left + GAP * 2,
      top: target.top + target.height - GAP * 2 - callout.height,
    };
  }
  return clampToScreen(placement, callout, screen);
}

function clampToScreen(
  placement: Placement,
  callout: { readonly width: number; readonly height: number },
  screen: { readonly width: number; readonly height: number },
): Placement {
  const clamp = (value: number, max: number) => Math.max(MARGIN, Math.min(value, max - MARGIN));
  return {
    side: placement.side,
    left: clamp(placement.left, screen.width - callout.width),
    top: clamp(placement.top, screen.height - callout.height),
  };
}
